import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  usePrincipal,
  useAuth,
  useApprovals,
  hasGrant,
  visibleSurfaces,
  PendingApprovalBanner,
  type Scope,
  type Surface,
} from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { Icon, type IconName } from "../ui/icons";
import { useUnreadCount } from "../surfaces/notifications/use-notifications";
import { SURFACES } from "./nav";

// The advisor console chrome, restructured to the Fable-approved information
// architecture (founder handoff): a persistent LEFT sidebar whose RBAC nav is
// grouped into FIVE clusters (Bugün · Müşteriler · İş Akışı · Finans & Uyum ·
// Hesap), a breadcrumb top bar with a ⌘K global search + a quiet notification
// dot + the TR/EN toggle + user menu, and a full-width surface canvas. The nav is
// still computed from the principal's grants via the foundation primitive — the
// clusters below are PRESENTATION only; nav.ts stays the routable source of truth,
// and the API remains the security boundary. DS is unchanged: only `csh-*`/`k-`
// classes and existing tokens are used — no new palette, no restyled buttons.

// The Fable IA clusters. Each lists the surface KEYS it groups + the outline glyph;
// a key only renders when the principal's grants make its surface visible, so an
// empty cluster (whole section RBAC-hidden) collapses. Surfaces absent from every
// cluster (teklif, book) stay routable but off the rail — folded into a flow.
type Cluster = { key: string; labelKey: MessageKey; items: { key: string; icon: IconName }[] };
const CLUSTERS: Cluster[] = [
  {
    key: "today",
    labelKey: "nav.section.today",
    items: [
      { key: "dashboard", icon: "dashboard" },
      { key: "queue", icon: "tasks" },
    ],
  },
  {
    key: "customers",
    labelKey: "nav.section.customers",
    items: [
      { key: "households", icon: "household" },
      { key: "relationships", icon: "suggestion" },
      { key: "intake", icon: "conversation" },
    ],
  },
  {
    key: "workflow",
    labelKey: "nav.section.workflow",
    items: [
      { key: "recommendations", icon: "suggestion" },
      { key: "issuance", icon: "file" },
      { key: "claims", icon: "protection" },
      { key: "renewals", icon: "calendar" },
    ],
  },
  {
    key: "finance",
    labelKey: "nav.section.finance",
    items: [
      { key: "approvals", icon: "check-circle" },
      { key: "reconciliation", icon: "scorecard" },
    ],
  },
  {
    key: "account",
    labelKey: "nav.section.account",
    items: [
      { key: "notifications", icon: "alert" },
      { key: "settings", icon: "member" },
    ],
  },
];

const SCOPE_RANK: Record<Scope, number> = { own: 1, team: 2, all: 3 };
// Cross-app hop to the admin console (visibility, not control). Config, not code —
// defaults to a relative /admin when the deployment doesn't set it.
const ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? "/admin";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function ConsoleShell({ children }: { children: ReactNode }) {
  const { me, isLoading, error } = usePrincipal();
  const { logout } = useAuth();
  const { pending } = useApprovals();
  // A8 — the real unread count behind the bell. A failed/absent read yields 0,
  // which renders the plain dot: the shell degrades to its previous behaviour
  // rather than showing a wrong number or an error chrome in the header.
  const { data: unreadData } = useUnreadCount(hasGrant(me, "queue:read"));
  const unread = unreadData?.unreadCount ?? 0;
  const { t, locale, setLocale } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const userRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  // ⌘K / Ctrl-K opens the global command/search palette; Escape closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (isLoading)
    return (
      <div className="csh-boot">
        <span className="k-loading__spinner" />
        <span>{t("principal.loading")}</span>
      </div>
    );
  if (error || !me)
    return (
      <div className="csh-boot" role="alert">
        {t("principal.error")}
      </div>
    );

  const visible = visibleSurfaces(me, SURFACES);
  const visibleByKey = new Map(visible.map((s) => [s.key, s]));
  const clusters = CLUSTERS.map((c) => ({
    ...c,
    items: c.items
      .map((it) => ({ ...it, surface: visibleByKey.get(it.key) }))
      .filter((it): it is typeof it & { surface: (typeof visible)[number] } => Boolean(it.surface)),
  })).filter((c) => c.items.length > 0);

  const current = visible.find(
    (s) => location.pathname === s.path || location.pathname.startsWith(s.path + "/"),
  );
  const name = me.displayName ?? me.sub;

  // Scope-computed role affordances (grants are the only signal; the API still
  // enforces). all-scope → the admin oversight strip + Admin Console link; team →
  // a light own↔team hint. own advisors get neither (RBAC already omits the rest).
  const maxScope = me.permissions.reduce((m, g) => Math.max(m, SCOPE_RANK[g.scope]), 0);
  const isAllScope = maxScope === SCOPE_RANK.all;
  const isTeamScope = maxScope === SCOPE_RANK.team;

  return (
    <div className="csh k-scope">
      <aside className="csh__sidebar">
        <div className="csh__brand">
          <span className="csh__brand-logo" aria-hidden="true">
            <Icon name="check" size={22} strokeWidth={2.4} />
          </span>
          <span className="csh__brand-text">
            <span className="csh__brand-name">CSA Privé</span>
            <span className="csh__brand-sub">{t("shell.brandSub")}</span>
          </span>
        </div>

        <nav aria-label="surfaces" className="csh__nav">
          {clusters.map((group) => (
            <div key={group.key} className="csh__group">
              <div className="csh__section">{t(group.labelKey)}</div>
              {group.items.map((it) => (
                <NavLink key={it.key} to={it.surface.path} className="csh__link" end={false}>
                  <Icon name={it.icon} size={19} />
                  <span>{t(it.surface.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {isAllScope && (
          <a className="csh__link csh__adminlink" href={ADMIN_URL} data-testid="admin-console-link">
            <Icon name="vault" size={19} />
            <span>{t("shell.admin.consoleLink")}</span>
          </a>
        )}

        <div className="csh__user" ref={userRef}>
          <button
            type="button"
            className="csh__userchip"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="csh__avatar" aria-hidden="true">
              {initials(name)}
            </span>
            <span className="csh__usermeta">
              <span className="csh__username">{name}</span>
              <span className="csh__userrole">{t("shell.role")}</span>
            </span>
            <Icon name="chevron-up" size={16} className="csh__userchevron" />
          </button>
          {menuOpen && (
            <div className="csh__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="csh__menuitem"
                onClick={() => void logout()}
              >
                <Icon name="lock" size={16} />
                <span>{t("auth.logout")}</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="csh__mainwrap">
        <header className="csh__topbar">
          <nav className="csh__crumbs" aria-label="breadcrumb">
            <span>{t("shell.brandSub")}</span>
            <Icon name="chevron-right" size={15} aria-hidden="true" />
            <span className="csh__crumb-current">
              {current ? t(current.labelKey) : t("shell.brandSub")}
            </span>
          </nav>
          {isTeamScope && (
            <span className="k-chip csh__scopehint" data-testid="scope-hint">
              {t("shell.scope.team")}
            </span>
          )}
          <span className="csh__spacer" />

          {/* ⌘K global search — opens the party-finder palette (wired to /households). */}
          <button
            type="button"
            className="k-search csh__cmdk"
            onClick={() => setPaletteOpen(true)}
            aria-label={t("shell.search.open")}
            data-testid="cmdk-trigger"
          >
            <Icon name="search" size={16} />
            <span className="csh__cmdk-label">{t("shell.search.placeholder")}</span>
            <kbd className="csh__kbd">⌘K</kbd>
          </button>

          {/* A8 — the notification indicator, now actually about NOTIFICATIONS.
              It previously rendered a dot driven by `pending` from the four-eyes
              approval context: a bell that lit up only during an in-flight
              approval handshake and was blind to the feed it navigates to. An
              advisor with nine unread signals saw the same neutral dot as one
              with none, so the control taught them nothing and they learned to
              ignore it.

              The dot is kept for the parked-approval case (a different, urgent
              thing) and the badge carries the unread count. Zero renders NO
              badge rather than a "0" — an empty inbox should look quiet. */}
          <button
            type="button"
            className="csh__notifbtn"
            aria-label={
              // `t` has no interpolation (foundation useT takes a key only), so the
              // count is composed rather than templated. Not worth a placeholder
              // system for one string.
              unread > 0
                ? `${t("shell.notifications.unread")}: ${unread}`
                : t(pending ? "shell.notifications.pending" : "shell.notifications.idle")
            }
            data-testid="notif-indicator"
            data-state={pending ? "pending" : "idle"}
            onClick={() => navigate("/notifications")}
          >
            <Icon name="alert" size={18} />
            {unread > 0 ? (
              <span className="csh__notifbadge" data-testid="notif-count">
                {unread > 99 ? "99+" : unread}
              </span>
            ) : (
              <span className={`csh__notifdot${pending ? " is-pending" : ""}`} aria-hidden="true" />
            )}
          </button>

          <div className="k-langtoggle" role="group" aria-label="language">
            <button
              type="button"
              className={`k-langtoggle__opt${locale === "tr" ? " is-active" : ""}`}
              onClick={() => setLocale("tr")}
              aria-pressed={locale === "tr"}
            >
              TR
            </button>
            <button
              type="button"
              className={`k-langtoggle__opt${locale === "en" ? " is-active" : ""}`}
              onClick={() => setLocale("en")}
              aria-pressed={locale === "en"}
            >
              EN
            </button>
          </div>
        </header>

        {/* all-scope oversight strip — calm amber (reuses the governance banner). Power
            is visibility, not control: off-price issuance + config stay four-eyes. */}
        {isAllScope && (
          <div
            role="note"
            className="banner banner-pending csh__oversight"
            data-testid="admin-strip"
          >
            {t("shell.admin.oversight")}
          </div>
        )}

        <PendingApprovalBanner />

        <main className="csh__main">{children}</main>
      </div>

      {paletteOpen && (
        <CommandPalette
          surfaces={visible}
          onClose={() => setPaletteOpen(false)}
          onNavigate={(to) => {
            setPaletteOpen(false);
            navigate(to);
          }}
        />
      )}
    </div>
  );
}

// A minimal ⌘K command/search palette: type to filter the visible surfaces, or press
// Enter to jump to the party finder (Müşterilerim). Presentation reuses k-overlay /
// k-dialog / k-search — no new visual vocabulary.
function CommandPalette({
  surfaces,
  onClose,
  onNavigate,
}: {
  surfaces: Surface<MessageKey>[];
  onClose: () => void;
  onNavigate: (to: string) => void;
}) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const matches = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase();
    if (!needle) return surfaces;
    return surfaces.filter((s) => t(s.labelKey).toLocaleLowerCase().includes(needle));
  }, [q, surfaces, t]);

  // The party finder = the households surface (the customers list is the finder).
  const finder = surfaces.find((s) => s.key === "households");

  return (
    <div className="k-overlay" role="dialog" aria-modal="true" aria-label={t("shell.search.open")}>
      <div className="k-dialog csh__palette">
        <form
          className="k-search csh__palette-search"
          onSubmit={(e) => {
            e.preventDefault();
            if (!finder) return;
            // Hand the typed query over as ?q=… The palette used to DROP it and land the
            // advisor on an empty search box — the hint below promises search-on-Enter,
            // so they had to type the customer's name twice.
            const target = q.trim()
              ? `${finder.path}?q=${encodeURIComponent(q.trim())}`
              : finder.path;
            onNavigate(target);
          }}
        >
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("shell.search.customers")}
            aria-label={t("shell.search.customers")}
          />
          <button
            type="button"
            className="csh__palette-close"
            onClick={onClose}
            aria-label={t("shell.search.close")}
            data-testid="palette-close"
          >
            <Icon name="close" size={16} />
          </button>
        </form>
        <p className="csh__palette-hint">{t("shell.search.hint")}</p>
        <ul className="csh__palette-list" role="listbox" aria-label={t("shell.search.open")}>
          {matches.length === 0 && (
            <li className="csh__palette-empty">{t("shell.search.empty")}</li>
          )}
          {matches.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                className="csh__palette-item"
                onClick={() => onNavigate(s.path)}
              >
                <Icon name="arrow-right" size={15} />
                <span>{t(s.labelKey)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
